// ChatPage.js - Update the main component
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  subscribeToChatMessages, 
  sendMessage, 
  getChatById,
  markMessagesAsRead 
} from '../services/chatService';
import { 
  ArrowLeft, 
  Send, 
  Clock, 
  CheckCheck,
  Package,
  IndianRupee,
  MessageCircle,
  User,
  AlertCircle
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const ChatPage = () => {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  
  const messagesEndRef = useRef(null);
  const unsubscribeRef = useRef(null);
  
  // Fetch chat details
  useEffect(() => {
    const loadChat = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!chatId || !user) {
          throw new Error('Missing chat ID or user');
        }
        
        const chatData = await getChatById(chatId);
        
        // Verify user is a participant
        const isParticipant = chatData.participants?.includes(user.uid) ||
                            chatData.buyerId === user.uid || 
                            chatData.sellerId === user.uid;
        
        if (!isParticipant) {
          throw new Error('You are not authorized to view this chat');
        }
        
        setChat(chatData);
        
        // Mark messages as read for current user
        await markMessagesAsRead(chatId, user.uid);
        
      } catch (err) {
        console.error('Error loading chat:', err);
        setError(err.message);
        toast.error(err.message || 'Failed to load chat');
        
        if (err.message.includes('not authorized') || err.message.includes('not found')) {
          setTimeout(() => navigate('/dashboard'), 2000);
        }
      } finally {
        setLoading(false);
      }
    };
    
    loadChat();
  }, [chatId, user, navigate]);
  
  // Subscribe to messages
  useEffect(() => {
    if (!chatId || !user) return;
    
    // Clean up previous subscription
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
    }
    
    try {
      unsubscribeRef.current = subscribeToChatMessages(chatId, (messageList) => {
        setMessages(messageList);
        
        // Auto-scroll to bottom
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      });
    } catch (err) {
      console.error('Error setting up message subscription:', err);
    }
    
    // Cleanup on unmount
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [chatId, user]);
  
  // Handle send message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || sending || !user || !chatId) {
      return;
    }
    
    setSending(true);
    
    try {
      // Determine user role in this chat
      const isBuyer = chat?.buyerId === user.uid;
      const senderRole = isBuyer ? 'buyer' : 'seller';
      const senderName = user.displayName || user.name || user.email?.split('@')[0] || 'User';
      
      await sendMessage(chatId, {
        senderId: user.uid,
        senderName: senderName,
        senderEmail: user.email || '',
        senderRole: senderRole,
        message: newMessage.trim(),
        type: 'text'
      });
      
      setNewMessage('');
      
      // Scroll to bottom after sending
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      
    } catch (err) {
      console.error('Error sending message:', err);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };
  
  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'short',
      year: diffDays > 365 ? 'numeric' : undefined
    });
  };
  
  // Get other user info
  const getOtherUserInfo = () => {
    if (!chat || !user) return null;
    
    const isBuyer = chat.buyerId === user.uid;
    
    return {
      name: isBuyer ? chat.sellerName : chat.buyerName,
      email: isBuyer ? chat.sellerEmail : chat.buyerEmail,
      role: isBuyer ? 'seller' : 'buyer',
      isVerified: isBuyer ? chat.sellerVerified : false
    };
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading conversation...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md p-6">
          <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Chat Not Available</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }
  
  const otherUser = getOtherUserInfo();
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Chat Header */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Go back"
              >
                <ArrowLeft className="h-5 w-5 text-gray-700" />
              </button>
              
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center">
                  <User className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <h1 className="font-semibold text-gray-900">{otherUser?.name || 'Chat'}</h1>
                    {otherUser?.isVerified && (
                      <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">
                        Verified
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 truncate max-w-xs">
                    {otherUser?.email || ''}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Product Info */}
            {chat?.productId && (
              <button
                onClick={() => navigate(`/buyer/products/${chat.productId}`)}
                className="hidden md:flex items-center space-x-2 px-4 py-2 bg-orange-50 text-orange-600 hover:bg-orange-100 rounded-lg transition-colors"
              >
                <Package className="h-4 w-4" />
                <span className="text-sm font-medium">View Product</span>
              </button>
            )}
          </div>
          
          {/* Product info banner for mobile */}
          {chat?.productName && (
            <div className="mt-3 md:hidden">
              <div className="text-sm text-gray-700 truncate">
                About: <span className="font-medium">{chat.productName}</span>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Chat Container */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Product Info Card */}
        {chat?.productName && (
          <div className="mb-6 bg-white rounded-lg shadow-sm p-4 border border-gray-200">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="font-medium text-gray-900 mb-2">{chat.productName}</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm text-gray-600">
                  <div className="flex items-center">
                    <Package className="h-3 w-3 mr-2 text-gray-400" />
                    <span>Product Inquiry</span>
                  </div>
                  {chat.productDetails?.quantity && (
                    <div className="flex items-center">
                      <span className="text-gray-500 mr-2">Qty:</span>
                      <span>{chat.productDetails.quantity} {chat.productDetails.unit}</span>
                    </div>
                  )}
                  {chat.productDetails?.totalPrice && (
                    <div className="flex items-center">
                      <IndianRupee className="h-3 w-3 mr-2 text-gray-400" />
                      <span>Total: ₹{chat.productDetails.totalPrice}</span>
                    </div>
                  )}
                </div>
              </div>
              
              <button
                onClick={() => navigate(`/buyer/products/${chat.productId}`)}
                className="ml-4 text-orange-600 hover:text-orange-700 text-sm font-medium"
              >
                View Details →
              </button>
            </div>
          </div>
        )}
        
        {/* Messages Area */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="h-[calc(100vh-350px)] overflow-y-auto p-4">
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-gray-500 p-8">
                <div className="h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                  <MessageCircle className="h-8 w-8 text-gray-400" />
                </div>
                <p className="font-medium text-lg mb-2">Start a conversation</p>
                <p className="text-sm text-center max-w-md">
                  Send your first message about {chat?.productName || 'this product'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((msg) => {
                  const isOwnMessage = msg.senderId === user?.uid;
                  
                  return (
                    <div
                      key={msg.id}
                      className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-xs md:max-w-md lg:max-w-lg rounded-2xl px-4 py-3 ${
                          isOwnMessage
                            ? 'bg-orange-100 text-gray-900 rounded-br-none'
                            : 'bg-gray-100 text-gray-900 rounded-bl-none'
                        }`}
                      >
                        {/* Sender info for others' messages */}
                        {!isOwnMessage && (
                          <div className="flex items-center mb-1">
                            <span className="text-sm font-medium text-gray-800">
                              {msg.senderName}
                            </span>
                            <span className="ml-2 text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full">
                              {msg.senderRole === 'buyer' ? 'Buyer' : 'Seller'}
                            </span>
                          </div>
                        )}
                        
                        {/* Message content */}
                        <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                        
                        {/* Inquiry details for initial message */}
                        {msg.type === 'inquiry' && msg.productDetails && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="text-xs text-gray-600 space-y-2">
                              <div className="flex justify-between">
                                <span>Product:</span>
                                <span className="font-medium text-gray-800">
                                  {msg.productDetails.productName}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span>Quantity:</span>
                                <span className="font-medium">
                                  {msg.productDetails.quantity} {msg.productDetails.unit}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span>Total Price:</span>
                                <span className="font-medium">
                                  ₹{msg.productDetails.totalPrice}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Message footer */}
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-gray-500">
                            {formatTime(msg.timestamp)}
                          </span>
                          {isOwnMessage && msg.readBy?.length > 1 && (
                            <div className="flex items-center ml-2">
                              <CheckCheck className="h-3 w-3 text-blue-500" />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
          
          {/* Message Input */}
          <form onSubmit={handleSendMessage} className="border-t border-gray-200 p-4">
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message here..."
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={sending}
                autoFocus
              />
              <button
                type="submit"
                disabled={sending || !newMessage.trim()}
                className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[100px] transition-colors"
              >
                {sending ? (
                  <>
                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send
                  </>
                )}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              Press Enter to send • Be professional and respectful
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChatPage;